import { Box, Typography, useTheme } from "@mui/material";
import {
  OptionGroupRoot,
  OptionsList,
  OptionButton,
} from "./OptionGroup.styles";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

export const OptionGroup = ({
  items,
  clickOnAction,
  onSelectOption,
  selectedMap, // context map
  cameras,
}) => {
  const theme = useTheme();
  const { language, optionLoading } = useSelectedOptions();

  // If context has a value for this group, use context to decide "active".
  // has NO selection in context yet (e.g., before initialization finishes).
  // Float-RGBA helper: "r=0.102,g=0.435,b=0.290,a=1"
  const isFloatRgbaString = (val) => {
    if (typeof val !== "string") return false;
    return /^r=\d*\.?\d+,\s*g=\d*\.?\d+,\s*b=\d*\.?\d+,\s*a=\d*\.?\d+$/i.test(
      val
    );
  };

  // Use only context to determine active state
  const computeIsActive = (opt, selectedMap) => {
    const descriptor = opt?.action?.descriptor;
    if (!descriptor) return false;

    const key = Object.keys(descriptor)[0];
    const selectedVal = selectedMap?.[key]?.descriptor?.[key];

    if (opt?.customRGB) {
      // active if the selected value equals the descriptor (e.g. "ipf")
      // OR if the selected value is a float RGBA string
      return selectedVal === descriptor[key] || isFloatRgbaString(selectedVal);
    }

    // normal (non-RGB) options: strict equality
    return selectedVal === descriptor[key];
  };

  return (
    <OptionGroupRoot>
      <OptionsList length={items?.length ?? 0}>
        {(items ?? []).map((opt, idx) => {
          const descriptor = opt?.action?.descriptor;
          const isActive = computeIsActive(opt, selectedMap);

          const disabled = false;

          const handleClick = () => {
            if (disabled) return; // block if disabled
            clickOnAction(descriptor);
            onSelectOption?.({
              name: opt?.name[language],
              image: opt?.image,
              price: opt?.price,
              descriptor,
              inSummary: opt?.inSummary,
            });
          };

          const canClick = !disabled && !isActive && !optionLoading;
          if (opt?.baseOption) return undefined;
          return (
            <>
              <OptionButton
                key={`${opt?.name[language]}${idx}`}
                title={opt?.name[language]}
                active={isActive && !disabled}
                disabled={!canClick}
                cameras={cameras}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  if (!canClick) return;

                  handleClick();
                }}
                sx={{
                  opacity: !canClick && !isActive ? 0.5 : 1,
                  cursor: !canClick ? "not-allowed" : "pointer",
                  pointerEvents: !canClick ? "none" : "auto",
                }}
              >
                <Box
                  component="img"
                  src={opt?.image}
                  alt={opt?.name[language]}
                />

                <Typography
                  variant="body1"
                  sx={{
                    textTransform: "none",
                    position: "absolute",
                    bottom: "-2rem",
                    fontWeight: 400,
                    color: theme?.palette?.companyColors?.secondary,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt?.name[language]}
                </Typography>
              </OptionButton>
            </>
          );
        })}
      </OptionsList>
    </OptionGroupRoot>
  );
};

export default OptionGroup;
